import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

export type UnsavedChangesState = "clean" | "saving" | "failed";

interface UnsavedChangesContextValue {
  state: UnsavedChangesState;
  isProtected: boolean;
  setProtectionState: (state: UnsavedChangesState) => void;
  shouldBlockNavigation: () => boolean;
  allowNextNavigation: () => void;
}

const defaultValue: UnsavedChangesContextValue = {
  state: "clean",
  isProtected: false,
  setProtectionState: () => undefined,
  shouldBlockNavigation: () => false,
  allowNextNavigation: () => undefined,
};

const UnsavedChangesContext = createContext<UnsavedChangesContextValue>(defaultValue);

export function UnsavedChangesProvider({ children }: PropsWithChildren) {
  const [state, setState] = useState<UnsavedChangesState>("clean");
  const stateRef = useRef<UnsavedChangesState>("clean");
  const allowNextNavigationRef = useRef(false);

  const setProtectionState = useCallback((nextState: UnsavedChangesState) => {
    stateRef.current = nextState;
    setState(nextState);
  }, []);

  const shouldBlockNavigation = useCallback(() => {
    if (allowNextNavigationRef.current) {
      allowNextNavigationRef.current = false;
      return false;
    }
    return stateRef.current !== "clean";
  }, []);

  const allowNextNavigation = useCallback(() => {
    allowNextNavigationRef.current = true;
  }, []);

  const value = useMemo(
    () => ({
      state,
      isProtected: state !== "clean",
      setProtectionState,
      shouldBlockNavigation,
      allowNextNavigation,
    }),
    [allowNextNavigation, setProtectionState, shouldBlockNavigation, state],
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
    </UnsavedChangesContext.Provider>
  );
}

// The provider and its hook intentionally share one module so consumers cannot
// import a second context instance during fast refresh.
// eslint-disable-next-line react-refresh/only-export-components
export function useUnsavedChanges() {
  return useContext(UnsavedChangesContext);
}
